import { defineStore } from 'pinia';

import { z } from 'zod';

import { useWithRoleAccessCheck } from '@/features/access-wrapper/withRoleAccessCheck';
import useMonitorObserverStore from '@/features/monitor-observer/useMonitorObserverStore';
import checkForUpdates from '@/features/version-update/update-availability/api/checkForUpdates';
import { Duration, MILLISECONDS } from '@/lib/date-time/duration';
import DefaultLogger from '@/lib/debug/logger/DefaultLogger';
import usePersistedQuery from '@/lib/fetch/usePersistedQuery';
import ApiResultStatus from '@/shared/api/api-result/ApiResultStatus';

export default defineStore('updateAvailability', () => {
  const monitorObserverStore = useMonitorObserverStore();
  const withRoleAccessCheck = useWithRoleAccessCheck();

  const {
    fetchingError: checkingForUpdatesError,
    isFetching: isCheckingForUpdates,
    refetch,
    value: isUpdateAvailable,
  } = usePersistedQuery({
    getInitialValue: () => false,
    parseValue: (value) => z.boolean().parse(value),
    queryFn: async () => {
      const result = await withRoleAccessCheck(checkForUpdates);

      monitorObserverStore.setIsObserverEnabled(result.status !== ApiResultStatus.SERVER_ERROR);

      if (result.status !== ApiResultStatus.OK) {
        DefaultLogger.writeError(result.errorMessage);
        throw new Error(result.errorMessage);
      }

      return result.isUpdateAvailable;
    },
    queryKey: 'updateAvailability',
    staleTime: Duration.HOUR * MILLISECONDS,
  });

  const recheckForUpdates = async ({
    onChecked,
    onError,
  }: {
    onChecked?: (isUpdateAvailable: boolean) => void;
    onError?: (errorMessage: string) => void;
  } = {}) => {
    await refetch();

    if (checkingForUpdatesError.value !== null) {
      onError?.(checkingForUpdatesError.value);
      return;
    }

    onChecked?.(isUpdateAvailable.value);
  };

  return {
    isCheckingForUpdates,
    isUpdateAvailable,
    recheckForUpdates,
  };
});
